"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useFormik } from "formik";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, ChevronRight, Send } from "@/components/icons";
import { fieldTouchedError } from "@/lib/form-helpers";
import { EMAIL, SALES_USE_CASES, SALES_BENEFITS } from "@/lib/constants";
import { contactSalesSchema } from "@/lib/form-schemas";
import { ContactPage } from "@/components/ui/contact-page";
import { FormError } from "@/components/ui/form-error";
import { FormSuccessPage } from "@/components/ui/form-success";
import { brandActionClasses } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ContactSalesSection() {
  const [submitted, setSubmitted] = useState(false);
  const [useCaseOpen, setUseCaseOpen] = useState(false);
  const useCaseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!useCaseOpen) return;
    function close(e: MouseEvent) {
      if (!useCaseRef.current?.contains(e.target as Node)) {
        setUseCaseOpen(false);
        formik.setFieldTouched("useCase", true);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setUseCaseOpen(false);
        document.getElementById("sales-usecase-trigger")?.focus();
      }
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [useCaseOpen]);

  const formik = useFormik({
    initialValues: { fullName: "", workEmail: "", useCase: "", message: "" },
    validationSchema: contactSalesSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const body = new FormData();
        body.append("name", values.fullName);
        body.append("email", values.workEmail);
        body.append("useCase", values.useCase);
        body.append("message", values.message);

        const res = await fetch("/api/contact/sales", { method: "POST", body });
        if (!res.ok) throw new Error("error");
        toast.success("Message sent!", {
          description: "Our sales team will be in touch within one business day.",
        });
        setSubmitted(true);
      } catch {
        toast.error("Something went wrong", {
          description: `Please try again or email ${EMAIL.sales}`,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (submitted) {
    return (
      <FormSuccessPage
        title="Message sent!"
        description="Our sales team will get back to you within one business day."
      />
    );
  }

  return (
    <ContactPage
      submitted={submitted}
      leftColumn={
        <>
          <h1 className="text-foreground mb-8 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Contact sales
          </h1>

          <ul className="mb-10 space-y-3">
            {SALES_BENEFITS.map((b) => (
              <li key={b} className="text-foreground flex items-center gap-2.5 text-sm">
                <CheckCircle2 size={16} className="text-brand shrink-0" />
                {b}
              </li>
            ))}
          </ul>

          <div className="border-border space-y-8 border-t pt-8">
            <div>
              <p className="text-muted-foreground mb-1.5 text-sm">
                Technical issues or product questions?
              </p>
              <Link
                href="/contact/support"
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                Contact support
                <ChevronRight size={14} />
              </Link>
            </div>

            <div>
              <p className="text-muted-foreground mb-1.5 text-sm">Prefer to reach us directly?</p>
              <a
                href={`mailto:${EMAIL.sales}`}
                className="text-foreground hover:text-primary inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200"
              >
                {EMAIL.sales}
                <ChevronRight size={14} />
              </a>
            </div>
          </div>
        </>
      }
      successContent={null}
      formContent={
        <form onSubmit={formik.handleSubmit} className="space-y-5 p-2 sm:p-8" noValidate>
          <div>
            <h2 className="text-foreground text-base font-semibold">Tell us how we can help</h2>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="fullName" className="text-muted-foreground block text-xs font-medium">
              Full name
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              autoComplete="name"
              placeholder="Jane Smith"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={!!fieldTouchedError(formik, "fullName")}
              aria-describedby={
                fieldTouchedError(formik, "fullName") ? "fullName-error" : undefined
              }
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "fullName") && (
              <FormError id="fullName-error">{fieldTouchedError(formik, "fullName")}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="workEmail" className="text-muted-foreground block text-xs font-medium">
              Work email
            </label>
            <input
              id="workEmail"
              name="workEmail"
              type="email"
              autoComplete="email"
              placeholder="jane@company.com"
              value={formik.values.workEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={!!fieldTouchedError(formik, "workEmail")}
              aria-describedby={
                fieldTouchedError(formik, "workEmail") ? "workEmail-error" : undefined
              }
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "workEmail") && (
              <FormError id="workEmail-error">{fieldTouchedError(formik, "workEmail")}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              id="sales-usecase-label"
              className="text-muted-foreground block text-xs font-medium"
            >
              Use case
            </label>
            <div ref={useCaseRef} className="relative">
              <button
                type="button"
                id="sales-usecase-trigger"
                aria-haspopup="listbox"
                aria-expanded={useCaseOpen}
                aria-controls="sales-usecase-listbox"
                aria-labelledby="sales-usecase-label sales-usecase-trigger"
                onClick={() => setUseCaseOpen((o) => !o)}
                className="border-border bg-background focus:ring-primary/40 flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
              >
                <span
                  className={formik.values.useCase ? "text-foreground" : "text-muted-foreground"}
                >
                  {formik.values.useCase || "Select a use case"}
                </span>
                <ChevronDown
                  size={14}
                  aria-hidden="true"
                  className="text-muted-foreground transition-transform duration-200"
                  style={{ transform: useCaseOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                />
              </button>

              {useCaseOpen && (
                <ul
                  id="sales-usecase-listbox"
                  role="listbox"
                  aria-labelledby="sales-usecase-label"
                  className="border-border bg-card absolute z-20 mt-1 w-full overflow-hidden rounded-lg border shadow-lg"
                >
                  {SALES_USE_CASES.map((uc) => (
                    <li key={uc} role="option" aria-selected={formik.values.useCase === uc}>
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue("useCase", uc);
                          setUseCaseOpen(false);
                        }}
                        className="text-foreground hover:bg-muted w-full px-3 py-2.5 text-left text-sm transition-colors"
                      >
                        {uc}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formik.touched.useCase && formik.errors.useCase && (
              <FormError>{formik.errors.useCase}</FormError>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="message" className="text-muted-foreground block text-xs font-medium">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              placeholder="Tell us about your project, expected volume, or any specific requirements…"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-invalid={!!fieldTouchedError(formik, "message")}
              aria-describedby={fieldTouchedError(formik, "message") ? "message-error" : undefined}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-primary/40 w-full resize-none rounded-lg border px-3 py-2.5 text-sm transition-shadow focus:ring-2 focus:outline-none"
            />
            {fieldTouchedError(formik, "message") && (
              <FormError id="message-error">{fieldTouchedError(formik, "message")}</FormError>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className={cn(
                brandActionClasses,
                "inline-flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
              )}
            >
              {formik.isSubmitting ? "Sending…" : "Send message"}
              {!formik.isSubmitting && <Send size={13} />}
            </button>
          </div>
        </form>
      }
    />
  );
}
